import React, { useState, useMemo, useEffect } from 'react';
import { Card, Select } from 'antd';
import { ComparisonRowTitleComponent, colorTableRow } from "./common";
import { stringMixin } from "../../utils/strings";
import DraggableTable from '../../components/Tables/DraggableTable';
import PnL from '../../components/PnL';


const { Option } = Select;
const TIME_FRAMES = ['1_yr', '3_yr', '5_yr'];

const DrawdownMetrics = ({ tickerData }) => {
  const [selectedTimeFrame, setSelectedTimeFrame] = useState('1_yr');
  const [data, setData] = useState([]);

  useEffect(() => {
    const newData = Object.entries(tickerData)
      .map(([symbol, data]) => ({
        key: symbol,
        symbol,
        ...data
      }));
    setData(newData);
  }, [tickerData]);

  const calculateDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    const startDate = new Date(start);
    const endDate = new Date(end);
    const durationInDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    return `${durationInDays} days`;
  };

  const columns = useMemo(() => [
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      render: (_, record) => <ComparisonRowTitleComponent symbol={record.symbol} record={record} />,
      width: 350,
      fixed: 'left'
    },
    {
      title: 'Max Drawdown',
      dataIndex: ['stats', 'windows', selectedTimeFrame, 'max_dd'],
      key: 'max_dd',
      render: (value) => <PnL value={value} />,
      sorter: (a, b) => (b.stats?.windows?.[selectedTimeFrame]?.max_dd ?? 0) - (a.stats?.windows?.[selectedTimeFrame]?.max_dd ?? 0),
    },
    {
      title: 'Max DD Duration',
      key: 'max_dd_duration',
      render: (_, record) => calculateDuration(
        record.stats?.windows?.[selectedTimeFrame]?.max_dd_start,
        record.stats?.windows?.[selectedTimeFrame]?.max_dd_end
      ),
      sorter: (a, b) => {
        const durationA = new Date(a.stats?.windows?.[selectedTimeFrame]?.max_dd_end) - new Date(a.stats?.windows?.[selectedTimeFrame]?.max_dd_start);
        const durationB = new Date(b.stats?.windows?.[selectedTimeFrame]?.max_dd_end) - new Date(b.stats?.windows?.[selectedTimeFrame]?.max_dd_start);
        return durationB - durationA;
      },
    },
    {
      title: 'Max DD Start',
      dataIndex: ['stats', 'windows', selectedTimeFrame, 'max_dd_start'],
      key: 'max_dd_start',
      render: (value) => value ? new Date(value).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Max DD End',
      dataIndex: ['stats', 'windows', selectedTimeFrame, 'max_dd_end'],
      key: 'max_dd_end',
      render: (value) => value ? new Date(value).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Avg Drawdown',
      dataIndex: ['stats', 'windows', selectedTimeFrame, 'avg_dd'],
      key: 'avg_dd',
      render: (value) => <PnL value={value} />,
      sorter: (a, b) => (b.stats?.windows?.[selectedTimeFrame]?.avg_dd ?? 0) - (a.stats?.windows?.[selectedTimeFrame]?.avg_dd ?? 0),
    },
    {
      title: 'Avg DD Duration',
      dataIndex: ['stats', 'windows', selectedTimeFrame, 'avg_dd_duration'],
      key: 'avg_dd_duration',
      render: (value) => value ? `${Math.round(value)} days` : 'N/A',
      sorter: (a, b) => (b.stats?.windows?.[selectedTimeFrame]?.avg_dd_duration ?? 0) - (a.stats?.windows?.[selectedTimeFrame]?.avg_dd_duration ?? 0),
    },
    {
      title: 'Pain',
      dataIndex: ['stats', 'windows', selectedTimeFrame, 'pain_index'],
      key: 'pain_index',
      render: (value) => stringMixin.pct(value),
      sorter: (a, b) => (b.stats?.windows?.[selectedTimeFrame]?.pain_index ?? 0) - (a.stats?.windows?.[selectedTimeFrame]?.pain_index ?? 0),
    },
  ], [selectedTimeFrame]);

  return (
    <Card
      bordered={false}
      title="Drawdown Metrics"
      style={{maxWidth: '95%'}}
      extra={
        <Select
          value={selectedTimeFrame}
          style={{ width: 120, marginBottom: 16 }}
          onChange={setSelectedTimeFrame}
        >
          {TIME_FRAMES.map(timeFrame => (
            <Option key={timeFrame} value={timeFrame}>{timeFrame.replace('_', ' ')}</Option>
          ))}
        </Select>
      }
    >
      <DraggableTable
        columns={columns}
        data={data}
        setData={setData}
        tableProps={{ onRow: colorTableRow, pagination: false, scroll: {y: 800, x: 'max-content'} }}
      />
    </Card>
  );
};

export default DrawdownMetrics;